/**
 * Multicolumn Icon - Mobile dots pagination
 */

class MulticolumnIconDots {
  constructor(section) {
    this.section = section;
    // The scrollable container is .swipe-mobile (which is the multicolumn-icon custom element)
    this.scrollContainer = section.querySelector('.swipe-mobile');
    // The inner grid container
    this.inner = section.querySelector('.swipe-mobile__inner');
    this.dotsContainer = section.querySelector('[data-dots-container]');
    this.dots = this.dotsContainer?.querySelectorAll('.multicolumn-icon__dot');

    // Debug logging
    console.log('MulticolumnIconDots init:', {
      hasScrollContainer: !!this.scrollContainer,
      hasInner: !!this.inner,
      hasDotsContainer: !!this.dotsContainer,
      dotsCount: this.dots?.length || 0,
      scrollContainerTag: this.scrollContainer?.tagName,
      scrollContainerClasses: this.scrollContainer?.className
    });

    if (!this.dotsContainer || !this.scrollContainer || !this.dots?.length || !this.inner) {
      console.warn('MulticolumnIconDots: Missing required elements');
      return;
    }

    this.mediaQuery = window.matchMedia('(max-width: 767.98px)');

    if (this.mediaQuery.matches) {
      this.init();
    }

    this.mediaQueryHandler = (e) => {
      if (e.matches) {
        this.init();
      } else {
        this.disable();
      }
    };
    this.mediaQuery.addEventListener('change', this.mediaQueryHandler);
  }

  init() {
    if (!this.scrollContainer || !this.dots || !this.inner) return;

    // Check if the scrollContainer actually scrolls, or if a child does
    const containerStyles = window.getComputedStyle(this.scrollContainer);
    if (containerStyles.overflowX !== 'auto' && containerStyles.overflowX !== 'scroll') {
      // Try to find the actual scrollable element
      const children = Array.from(this.scrollContainer.children);
      for (const child of children) {
        const childStyles = window.getComputedStyle(child);
        if (childStyles.overflowX === 'auto' || childStyles.overflowX === 'scroll') {
          console.log('Found scrollable child:', child.tagName, child.className);
          this.scrollContainer = child;
          break;
        }
      }
    }

    // Get slides from the inner container
    this.slides = this.inner.querySelectorAll('.f-column');
    console.log('MulticolumnIconDots init():', {
      slidesCount: this.slides.length,
      scrollContainer: this.scrollContainer.tagName,
      scrollContainerClasses: this.scrollContainer.className,
      scrollLeft: this.scrollContainer.scrollLeft,
      scrollWidth: this.scrollContainer.scrollWidth,
      clientWidth: this.scrollContainer.clientWidth,
      overflowX: window.getComputedStyle(this.scrollContainer).overflowX
    });
    if (!this.slides.length) return;

    // Wait for layout to be ready before calculating
    setTimeout(() => {
      this.updateSlideWidth();
      this.updateActiveDot();
    }, 100);

    this.scrollHandler = () => {
      console.log('Scroll event fired:', this.scrollContainer.scrollLeft);
      window.requestAnimationFrame(() => this.updateActiveDot());
    };
    // Listen to scroll on the swipe-mobile container (the actual scrolling element)
    this.scrollContainer.addEventListener('scroll', this.scrollHandler, { passive: true });
    console.log('Added scroll listener to:', this.scrollContainer.tagName);

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.scrollToSlide(index));
    });

    // Initial update after a short delay to ensure layout is ready
    setTimeout(() => {
      this.updateActiveDot();
    }, 150);

    this.resizeObserver = new ResizeObserver(() => {
      setTimeout(() => {
        this.updateSlideWidth();
        this.updateActiveDot();
      }, 50);
    });
    this.resizeObserver.observe(this.scrollContainer);
    
    // Periodic check as fallback (custom elements might not fire scroll events reliably)
    let lastScrollLeft = this.scrollContainer.scrollLeft || 0;
    this.updateInterval = setInterval(() => {
      const currentScrollLeft = this.scrollContainer.scrollLeft || 0;
      if (currentScrollLeft !== lastScrollLeft) {
        lastScrollLeft = currentScrollLeft;
        this.updateActiveDot();
      }
    }, 200);
  }

  updateSlideWidth() {
    if (this.slides && this.slides.length > 0 && this.inner && this.scrollContainer) {
      // For swipe-mobile, calculate based on actual scrollable width
      const firstSlide = this.slides[0];
      const styles = window.getComputedStyle(this.inner);
      const gap =
        parseFloat(styles.columnGap || '0') ||
        parseFloat(styles.gap || '0') ||
        0;

      // Get the actual width including gap
      this.slideWidth = firstSlide.offsetWidth + gap;
      
      // Alternative: calculate from scroll width if available
      if (this.scrollContainer.scrollWidth > 0 && this.slides.length > 0) {
        const totalScrollWidth = this.scrollContainer.scrollWidth;
        const calculatedWidth = totalScrollWidth / this.slides.length;
        // Use the more accurate calculation
        if (calculatedWidth > 0) {
          this.slideWidth = calculatedWidth;
        }
      }
    }
  }

  updateActiveDot() {
    if (!this.scrollContainer || !this.dots || !this.slides) return;
    
    // Recalculate slide width if not set
    if (!this.slideWidth) {
      this.updateSlideWidth();
    }
    
    // Find which slide is currently most visible
    let activeIndex = 0;
    let minDistance = Infinity;
    
    this.slides.forEach((slide, index) => {
      const slideRect = slide.getBoundingClientRect();
      const containerRect = this.scrollContainer.getBoundingClientRect();
      
      // Calculate how centered the slide is in the viewport
      const slideCenter = slideRect.left + slideRect.width / 2;
      const containerCenter = containerRect.left + containerRect.width / 2;
      const distance = Math.abs(slideCenter - containerCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        activeIndex = index;
      }
    });
    
    // Ensure index is within bounds
    activeIndex = Math.min(Math.max(0, activeIndex), this.dots.length - 1);

    this.dots.forEach((dot, index) => {
      dot.classList.toggle('multicolumn-icon__dot--active', index === activeIndex);
    });
  }

  scrollToSlide(index) {
    if (!this.scrollContainer || index < 0 || index >= this.slides.length) return;
    
    // Recalculate if needed
    if (!this.slideWidth) {
      this.updateSlideWidth();
    }
    
    if (!this.slideWidth) {
      // Fallback: scroll to the slide element directly
      const targetSlide = this.slides[index];
      if (targetSlide) {
        targetSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
      return;
    }

    const targetScrollLeft = index * this.slideWidth;
    this.scrollContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }

  disable() {
    if (this.scrollContainer && this.scrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }
}

function initMulticolumnIconDots() {
  // Find all multicolumn-icon sections
  document.querySelectorAll('.section.multicolumn-icon, [class*="multicolumn-icon"]').forEach((section) => {
    if (!section.dataset.multicolumnIconDotsInitialized) {
      section.dataset.multicolumnIconDotsInitialized = 'true';
      // Only init if dots markup exists
      if (section.querySelector('[data-dots-container]')) {
        new MulticolumnIconDots(section);
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMulticolumnIconDots);
} else {
  initMulticolumnIconDots();
}

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector('.section.multicolumn-icon, [class*="multicolumn-icon"]');
  if (section) {
    section.dataset.multicolumnIconDotsInitialized = 'false';
    initMulticolumnIconDots();
  }
});
